import logger from '~/services/logger';

const DELAY_BETWEEN_COMPONENT_MOUNTS_MS = 300;

/**
 * Shared queue to limit the rate of loading components.
 * This prevents multiple components from loading at the same time
 * and overwhelming the browser.
 */
const loadQueue: (() => void)[] = [];
let queueTimer: NodeJS.Timeout | null = null;

function processQueue() {
  if (queueTimer || loadQueue.length === 0) return;

  queueTimer = setTimeout(() => {
    const next = loadQueue.shift();
    if (next) next();

    queueTimer = null;
    processQueue();
  }, DELAY_BETWEEN_COMPONENT_MOUNTS_MS);
}

/**
 * Enqueue a function to be called later.
 * @param fn - Function to enqueue for loading.
 */
export function enqueueLoad(fn: () => void) {
  loadQueue.push(fn);
  processQueue();
}

export let sharedObserver: IntersectionObserver | null = null;
export const sharedObserverListeners = new Map<Element, () => void>();

export function getSharedObserver({
  threshold = 0.25,
}: {
  threshold?: number;
}): IntersectionObserver | null {
  try {
    if (sharedObserver) return sharedObserver;

    sharedObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const cb = sharedObserverListeners.get(entry.target);
            if (cb && sharedObserver) {
              cb();
              sharedObserver.unobserve(entry.target);
              sharedObserverListeners.delete(entry.target);
            }
          }
        }
      },
      {
        threshold,
        rootMargin: '100px 0px',
      }
    );

    return sharedObserver;
  } catch (error: any) {
    logger.error(`getSharedObserver: ${error.message}`);
    return null;
  }
}
